import {
  DoneAll,
  Notifications as NotificationsIcon,
  SettingsOutlined,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useMarkAllPushNotificationsAsRead,
  usePushNotificationCounts,
  usePushNotifications,
} from "../../hooks/usePushNotifications";
import NotificationItem from "./NotificationItem";

export default function NotificationCenter() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const {
    data: notifications = [],
    isLoading,
    error,
  } = usePushNotifications({
    limit: 20,
  });
  const { data: notificationCounts } = usePushNotificationCounts();
  const unreadCount = notificationCounts?.unread ?? 0;
  const markAllAsReadMutation = useMarkAllPushNotificationsAsRead();

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          ref={anchorRef}
          color="inherit"
          onClick={handleOpen}
          sx={{ position: "relative" }}
        >
          <Badge
            badgeContent={unreadCount > 99 ? "99+" : unreadCount}
            color="error"
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        aria-labelledby="notifications-popover"
        aria-label="Notifications-popover"
        aria-describedby="notifications-popover-description"
        anchorEl={anchorEl || anchorRef.current}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxWidth: "90vw",
              maxHeight: 600,
              mt: 1,
            },
          },
        }}
      >
        {/* Notification Center Header */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Notifications</Typography>
            <Box>
              {unreadCount > 0 && (
                <Tooltip title="Mark all as read">
                  <IconButton
                    size="small"
                    onClick={handleMarkAllAsRead}
                    disabled={markAllAsReadMutation.isPending}
                  >
                    <DoneAll />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Settings">
                <IconButton
                  onClick={() => {
                    navigate("/settings?tab=notifications");
                    handleClose();
                  }}
                >
                  <SettingsOutlined />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        <Divider />
        {/* Notification Center Content */}
        <Box
          sx={{
            maxHeight: 500,
            overflowY: "auto",
          }}
        >
          {isLoading ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                fontSize="0.93rem"
              >
                Oops!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Something went wrong while loading notifications. Please try
                again later.
              </Typography>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  handleClose={handleClose}
                />
              ))}
            </List>
          )}
        </Box>
        {/* Notification Center Footer */}
        <Divider />
        <Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              component={Typography}
              variant="text"
              onClick={() => {
                navigate("/notifications");
                handleClose();
              }}
              sx={{
                textDecoration: "underline",
                color: "text.secondary",
              }}
            >
              View all notifications
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
